import _ from 'lodash';
import { DepartmentKey } from 'models';
import { createContext, useEffect, useState } from 'react';
import { Button, Col, Form, Modal, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import organizationTreeService from '../../features/common/organizationTreeService';
import { DepartmentDatePicker, DepartmentMenu } from './components';
import { OrganizationTreeFilter } from './models/common';
import { CompanyNode } from './models/company';
import { DepartmentNode } from './models/department';
import { RestaurantNode } from './models/restaurant';
import './style.scss';
import { findNode, findParentKeys, getDepartmentKey } from './utils';

type DepartmentMenuContextType = {
  path: DepartmentKey[];
  allowSelectTypes?: Array<string | null>;
  activeDepartmentKey?: DepartmentKey;
  changeSelectedDepartment: ((data: CompanyNode | DepartmentNode) => void) | undefined;
};

export const DepartmentMenuContext = createContext<DepartmentMenuContextType>({
  path: [],
  allowSelectTypes: [null, 'A', 'B', 'T'],
  activeDepartmentKey: undefined,
  changeSelectedDepartment: undefined,
});

export interface SelectSingleRestaurantModalProps {
  isModal?: boolean;
  defaultDataDate?: Date;
  showDataDatePicker?: boolean;
  allowSelectTypes?: Array<string | null>;
  defaultActiveDepartmentKey?: DepartmentKey;
  onClose?: () => void;
  onReflect?: (department: CompanyNode | DepartmentNode | RestaurantNode) => void;
}

function SelectSingleRestaurantModal({
  isModal = false,
  defaultDataDate = new Date(),
  showDataDatePicker = true,
  allowSelectTypes = [null, 'A', 'B', 'T'],
  defaultActiveDepartmentKey = { id: 0, cd: '' },
  onClose,
  onReflect,
}: SelectSingleRestaurantModalProps) {
  const { t } = useTranslation();

  //state
  const [organizationData, setOrganizationData] = useState<CompanyNode[] | DepartmentNode[]>([]);
  const [activeDepartmentKey, setActiveDepartmentKey] = useState<DepartmentKey>(defaultActiveDepartmentKey);

  const [filter, setFilter] = useState<OrganizationTreeFilter>({
    dataDate: defaultDataDate,
  });
  const initPathMenu = findParentKeys(organizationData, defaultActiveDepartmentKey.id, defaultActiveDepartmentKey.cd);
  const [pathMenu, setPathMenu] = useState<DepartmentKey[]>(
    initPathMenu ? [...initPathMenu, defaultActiveDepartmentKey] : []
  );
  const [selectedDepartment, setSelectedDepartment] = useState<CompanyNode | DepartmentNode | undefined>();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const response = await organizationTreeService.getAll(filter);
      setOrganizationData(response.data);
      setLoading(false);
    })();
  }, [filter]);

  useEffect(() => {
    if (organizationData.length > 0 && activeDepartmentKey.id !== 0 && activeDepartmentKey.cd !== '') {
      const initPathMenu = findParentKeys(organizationData, activeDepartmentKey.id, activeDepartmentKey.cd);
      setPathMenu(initPathMenu ? [...initPathMenu, activeDepartmentKey] : []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationData, activeDepartmentKey]);

  useEffect(() => {
    setSelectedDepartment(findNode(organizationData, activeDepartmentKey.id, activeDepartmentKey.cd));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationData]);

  useEffect(() => {
    if (
      activeDepartmentKey.id !== 0 &&
      activeDepartmentKey.cd !== '' &&
      selectedDepartment &&
      !_.isEqual(activeDepartmentKey, getDepartmentKey(selectedDepartment))
    ) {
      const department = findNode(organizationData, activeDepartmentKey.id, activeDepartmentKey.cd);
      if (department) {
        // onReflect && onReflect(branch, filter.dataDate);
        setSelectedDepartment(department);
        setActiveDepartmentKey(getDepartmentKey(department));

        //scroll to node
        document.getElementById(`branch-${activeDepartmentKey.id}-${activeDepartmentKey.cd}`)?.scrollIntoView({
          behavior: 'auto',
          block: 'center',
          inline: 'center',
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDepartmentKey]);

  function handleClose() {
    onClose && onClose();
  }

  function handleDateChanged(date: Date) {
    const newFilter: OrganizationTreeFilter = {
      ...filter,
      dataDate: date,
    };
    setFilter(newFilter);
  }

  function handleSelectedDepartmentChanged(department: CompanyNode | DepartmentNode | RestaurantNode) {
    const departmentKey = getDepartmentKey(department);
    const parentMenuKeys = findParentKeys(organizationData, departmentKey.id, departmentKey.cd);
    if (pathMenu.some((pathItem) => pathItem.id === departmentKey.id && pathItem.cd === departmentKey.cd)) {
      if (parentMenuKeys) {
        setPathMenu([...parentMenuKeys, departmentKey]);
      } else {
        setPathMenu([]);
      }
    } else {
      if (parentMenuKeys) {
        setPathMenu([...parentMenuKeys, departmentKey]);
      } else {
        setPathMenu([departmentKey]);
      }
    }
    setSelectedDepartment(department);
    setActiveDepartmentKey(getDepartmentKey(department));

    !isModal && onReflect && onReflect(department);
  }

  function handleReflect() {
    if (selectedDepartment) {
      onReflect && onReflect(selectedDepartment);
      handleClose();
    }
  }

  const modalBody = (
    <div className="branch-menu">
      {showDataDatePicker && (
        <div className="mb-3">
          <Row>
            <Col sm={3}>
              <Form.Label className="col-form-label">{t('SelectSingleRestaurantModal.target_date')}：</Form.Label>
            </Col>
            <Col sm={9}>
              <DepartmentDatePicker defaultDate={filter.dataDate} onDateChanged={handleDateChanged} />
            </Col>
          </Row>
        </div>
      )}
      <div className="mb-2">
        <strong>{t('SelectSingleRestaurantModal.target_store')}</strong>
      </div>
      {isModal && (
        <div className="text-end mb-2">
          <Button className="btn--dark-green" onClick={handleReflect}>
            {t('SelectSingleRestaurantModal.selection')}
          </Button>
        </div>
      )}

      {!loading ? (
        <DepartmentMenuContext.Provider
          value={{
            path: pathMenu,
            allowSelectTypes: allowSelectTypes,
            activeDepartmentKey: activeDepartmentKey,
            changeSelectedDepartment: handleSelectedDepartmentChanged,
          }}
        >
          <div className="department-menu-wrapper" style={{ minHeight: '100px', maxHeight: '480px' }}>
            {organizationData.length > 0 && <DepartmentMenu data={organizationData} />}
            {organizationData.length === 0 && <span>{t('SelectSingleRestaurantModal.no_data_found')}</span>}
          </div>
        </DepartmentMenuContext.Provider>
      ) : (
        <div className="department-menu-wrapper" style={{ minHeight: '100px', maxHeight: '480px' }}>
          <span>{t('App.loading')}</span>
        </div>
      )}
    </div>
  );

  if (isModal) {
    return (
      <Modal size="sm" show backdrop="static" onHide={handleClose} centered>
        <Modal.Header closeButton>
          <div className="page-head-title bg-violet p-2 fw-bold" style={{ minWidth: '150px' }}>
            {t('SelectSingleRestaurantModal.head_title')}
          </div>
        </Modal.Header>
        <Modal.Body>{modalBody}</Modal.Body>
      </Modal>
    );
  }

  return modalBody;
}

export default SelectSingleRestaurantModal;
